import { composeBriefIntro as composeBriefIntroI18n } from '@/lib/neoChatI18n';
import { buildComposeViaPersonaPagePath } from '@/lib/chatbotDeepLinks';

const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://api.neogtm.com';
    }
  }
  return 'https://api.neogtm.com';
})();

function chatbotHeaders(tenantId, userId) {
  const headers = { 'Content-Type': 'application/json' };
  if (tenantId) headers['x-tenant-id'] = tenantId;
  if (userId) headers['x-user-id'] = userId;
  return headers;
}

export function composeBriefIntro(greeting, userFirstName, itemCount, language = null) {
  return composeBriefIntroI18n(greeting, userFirstName, itemCount, language);
}

export function normalizeBriefItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const accountName = String(raw.account_name ?? raw.accountName ?? '').trim();
  const accountId = raw.account_id ?? raw.accountId ?? null;
  const sentence = String(
    raw.sentence ?? raw.summary ?? raw.description ?? raw.rationale ?? raw.body ?? '',
  ).trim();
  const chip = raw.chip && typeof raw.chip === 'object' ? raw.chip : {};
  const chipLabel = String(
    chip.label ?? raw.chip_label ?? raw.chipLabel ?? raw.action_label ?? '',
  ).trim();
  const chipActionType = String(chip.action_type ?? chip.actionType ?? 'navigate').trim();
  const chipTargetType = String(chip.target_type ?? chip.targetType ?? '').trim();
  const chipTargetSubtype = String(chip.target_subtype ?? chip.targetSubtype ?? '').trim();
  const chipTargetId = chip.target_id ?? chip.targetId ?? null;
  const personNameRaw = raw.person_name ?? raw.personName ?? null;
  const personName =
    personNameRaw != null && String(personNameRaw).trim() !== ''
      ? String(personNameRaw).trim()
      : null;
  const id = raw.id ?? raw.recommendation_id ?? raw.recommendationId ?? null;
  const tenantId = raw.tenant_id ?? raw.tenantId ?? null;
  if (!accountName && !sentence && !chipLabel) return null;
  return {
    id: id != null ? String(id) : null,
    accountName,
    accountId: accountId != null ? String(accountId) : null,
    sentence,
    chipLabel,
    chipActionType,
    chipTargetType,
    chipTargetSubtype: chipTargetSubtype || null,
    chipTargetId: chipTargetId != null ? String(chipTargetId) : null,
    personName,
    tenantId: tenantId != null ? String(tenantId).toLowerCase() : null,
  };
}

export function buildMorningBriefMessage(briefResponse, tenantId, language = null) {
  const payload =
    briefResponse && typeof briefResponse === 'object' && !Array.isArray(briefResponse)
      ? briefResponse
      : { items: briefResponse };
  const normalized = (Array.isArray(payload.items) ? payload.items : [])
    .map(normalizeBriefItem)
    .filter(Boolean)
    .slice(0, 5);
  if (normalized.length === 0) return null;
  const scopedTenant = tenantId != null ? String(tenantId).toLowerCase() : null;
  return {
    sender: 'bot',
    type: 'morning_brief',
    text: composeBriefIntro(
      payload.greeting,
      payload.user_first_name ?? payload.userFirstName,
      normalized.length,
      language,
    ),
    briefItems: normalized,
    tenantId: scopedTenant,
    id: `morning-brief-${scopedTenant || 'default'}-${Date.now()}`,
  };
}

export { buildBriefTargetPageUrl } from '@/lib/chatbotDeepLinks';

export async function fetchChatbotBrief(tenantId, userId, { language } = {}) {
  if (!tenantId || !userId) return null;
  const params = new URLSearchParams({
    tenant_id: tenantId,
    user_id: userId,
  });
  if (language) params.set('language', String(language));
  const res = await fetch(`${API_BASE_URL}/api/chatbot/brief?${params.toString()}`, {
    credentials: 'include',
    headers: chatbotHeaders(tenantId, userId),
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

export async function fetchChatbotBriefCountOnly(tenantId, userId, { language } = {}) {
  if (!tenantId || !userId) return { unviewed_count: 0 };
  const params = new URLSearchParams({
    tenant_id: tenantId,
    user_id: userId,
    count_only: 'true',
  });
  if (language) params.set('language', String(language));
  const res = await fetch(`${API_BASE_URL}/api/chatbot/brief?${params.toString()}`, {
    credentials: 'include',
    headers: chatbotHeaders(tenantId, userId),
  });
  if (!res.ok) return { unviewed_count: 0 };
  const data = await res.json().catch(() => ({}));
  return {
    unviewed_count: Number(data?.unviewed_count) || 0,
  };
}

export function markChatbotBriefViewed(tenantId, userId) {
  if (!tenantId || !userId) return;
  fetch(`${API_BASE_URL}/api/chatbot/brief/mark-viewed`, {
    method: 'POST',
    credentials: 'include',
    headers: chatbotHeaders(tenantId, userId),
    body: JSON.stringify({ tenant_id: tenantId, user_id: userId }),
  }).catch(() => {});
}

export function postChatbotRecommendationAction(recommendationId, action, tenantId, userId) {
  const id = recommendationId != null ? String(recommendationId).trim() : '';
  if (!id || !tenantId || !userId) return;
  fetch(`${API_BASE_URL}/api/chatbot/recommendation/${encodeURIComponent(id)}/action`, {
    method: 'POST',
    credentials: 'include',
    headers: chatbotHeaders(tenantId, userId),
    body: JSON.stringify({ action, tenant_id: tenantId, user_id: userId }),
  }).catch(() => {});
}

/** Resolve chatbot_recommendations.id from a Today's Plan / brief row. */
export function resolveRecommendationId(entity) {
  if (!entity || typeof entity !== 'object') return null;
  const metadata =
    entity.metadata && typeof entity.metadata === 'object' ? entity.metadata : null;
  const raw =
    entity.recommendation_id ??
    entity.recommendationId ??
    entity.rec_id ??
    entity.recId ??
    metadata?.recommendation_id ??
    metadata?.recommendationId ??
    entity.recommendation?.id ??
    entity.chatbot_recommendation_id ??
    entity.chatbotRecommendationId ??
    null;

  if (raw != null && String(raw).trim() !== '') {
    return String(raw).trim();
  }

  // Plan rows may expose chatbot_recommendations.id as `id` (not persona id).
  if (entity.id != null && String(entity.id).trim() !== '') {
    const id = String(entity.id).trim();
    const personaId = entity.personaId ?? entity.person_id ?? entity.persona_id ?? null;
    if (personaId != null && String(personaId).trim() === id) {
      return null;
    }
    return id;
  }

  return null;
}

export class RecommendationComposeNotFoundError extends Error {
  constructor(message = 'No contact on file at this account') {
    super(message);
    this.name = 'RecommendationComposeNotFoundError';
    this.status = 404;
  }
}

/**
 * POST /api/chatbot/recommendation-compose-brief for a plan recommendation row.
 * Body: { tenant_id, user_id, recommendation_id } only.
 */
export async function fetchRecommendationComposeBrief({
  tenantId,
  userId,
  recommendationId,
} = {}) {
  if (!tenantId || !userId) {
    throw new Error('Missing tenant or user');
  }

  const recId = recommendationId != null ? String(recommendationId).trim() : '';
  if (!recId) {
    throw new Error('Missing recommendation id');
  }

  const res = await fetch(`${API_BASE_URL}/api/chatbot/recommendation-compose-brief`, {
    method: 'POST',
    credentials: 'include',
    headers: chatbotHeaders(tenantId, userId),
    body: JSON.stringify({
      tenant_id: tenantId,
      user_id: userId,
      recommendation_id: recId,
    }),
  });

  if (res.status === 404) {
    throw new RecommendationComposeNotFoundError();
  }

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(errText || `Could not compose brief (${res.status})`);
  }

  const data = await res.json();
  if (data?.success === false) {
    throw new Error(data?.error || data?.message || 'Could not compose brief');
  }

  return data;
}

/** PersonProfile path with custom context + auto-generate (compose_via_persona UX). */
export function buildRecommendationComposePagePath(result) {
  return buildComposeViaPersonaPagePath({
    personId: result?.target_person_id ?? result?.targetPersonId,
    accountId: result?.target_account_id ?? result?.targetAccountId,
    composeBrief: result?.compose_brief ?? result?.composeBrief ?? '',
    autoGenerate: true,
  });
}

export async function openRecommendationComposeDraft({
  tenantId,
  userId,
  recommendationId,
} = {}) {
  const result = await fetchRecommendationComposeBrief({
    tenantId,
    userId,
    recommendationId,
  });
  const pagePath = buildRecommendationComposePagePath(result);
  if (!pagePath) throw new Error('No persona target returned');
  return pagePath;
}
